import { tv } from 'tailwind-variants';
import { t } from '../../state/i18n';
import { dismissSplash, showSplash } from '../../state/splash';
import { MouseIcon, type MouseType } from './MouseIcon';

const overlay = tv({
  base: 'fixed inset-0 flex items-center justify-center z-[10000]',
});

const background = tv({
  base: 'absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950',
});

const orb = tv({
  base: 'absolute rounded-full blur-3xl animate-pulse',
});

const content = tv({
  base: 'relative flex flex-col items-center gap-6 p-12 animate-fade-in',
});

const title = tv({
  base: "text-5xl font-bold tracking-wide text-white/90 font-['Cinzel_Decorative']",
});

const subtitle = tv({
  base: 'text-sm font-mono text-slate-400/80',
});

const controlsGrid = tv({
  base: 'flex flex-col gap-3 mt-2 text-base',
});

const controlRow = tv({
  base: 'flex items-center gap-4',
});

const keyBadge = tv({
  base: [
    'inline-flex items-center justify-center',
    'min-w-[3em] px-3 py-1.5',
    'text-base font-mono font-bold leading-tight',
    'bg-slate-800/80 border border-slate-500/40',
    'rounded',
    'text-slate-200',
    'shadow-[0_1px_0_0_rgba(0,0,0,0.5)]',
  ],
});

const mouseBadge = tv({
  base: [
    'inline-flex items-center gap-2',
    'px-3 py-1.5',
    'text-base font-mono font-bold leading-tight',
    'bg-slate-800/80 border border-slate-500/40',
    'rounded',
    'text-slate-200',
    'shadow-[0_1px_0_0_rgba(0,0,0,0.5)]',
  ],
});

const controlLabel = tv({
  base: 'text-base text-slate-400/80 whitespace-nowrap',
});

const ctaButton = tv({
  base: [
    'mt-8 cursor-pointer',
    'w-64 py-4',
    'rounded-lg',
    'text-lg font-semibold tracking-wide',
    'text-white',
    'bg-gradient-to-b from-white/[0.12] to-white/[0.04]',
    'border border-white/20',
    'backdrop-blur-sm',
    'shadow-[0_0_30px_rgba(255,255,255,0.05),inset_0_1px_0_rgba(255,255,255,0.15)]',
    'transition-all duration-300',
    'hover:from-white/[0.18] hover:to-white/[0.08]',
    'hover:border-white/40',
    'hover:shadow-[0_0_40px_rgba(255,255,255,0.1),inset_0_1px_0_rgba(255,255,255,0.25)]',
    'active:scale-[0.97] active:from-white/[0.08] active:to-white/[0.02]',
  ],
});

const thanks = tv({
  base: 'text-slate-500 text-xs font-mono mt-2',
});

const KeyEntry = ({ badge, text }: { badge: string; text: string }) => (
  <div class={controlRow()}>
    <span class={keyBadge()}>{badge}</span>
    <span class={controlLabel()}>{text}</span>
  </div>
);

const MouseEntry = ({ type, badge, text }: { type: MouseType; badge: string; text: string }) => (
  <div class={controlRow()}>
    <span class={mouseBadge()}>
      <MouseIcon type={type} />
      {badge}
    </span>
    <span class={controlLabel()}>{text}</span>
  </div>
);

export const WelcomeSplash = () => {
  if (!showSplash.value) {
    return null;
  }

  return (
    <div class={overlay()}>
      {/* Animated background */}
      <div class={background()}>
        <div class="absolute inset-0 opacity-30">
          <div
            class={orb({ class: 'top-1/4 left-1/4 w-96 h-96 bg-teal-600/20' })}
            style="animation-duration: 4s"
          />
          <div
            class={orb({ class: 'bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600/20' })}
            style="animation-duration: 5s; animation-delay: 1s"
          />
        </div>
      </div>

      {/* Content */}
      <div class={content()}>
        <h1 class={title()}>{t('ui.splash.title')}</h1>
        <p class={subtitle()}>{t('ui.splash.subtitle')}</p>

        {/* Compact controls reference */}
        <div class={controlsGrid()}>
          <KeyEntry badge="WASD" text={t('ui.splash.controls.move')} />
          <MouseEntry type="left" badge="LMB" text={t('ui.splash.controls.select')} />
          <MouseEntry type="right" badge="RMB" text={t('ui.splash.controls.command')} />
          <KeyEntry badge="ESC" text={t('ui.splash.controls.menu')} />
        </div>

        {/* CTA button */}
        <button type="button" onClick={dismissSplash} class={ctaButton()}>
          {t('ui.splash.enterWorld')}
        </button>

        <p class={thanks()}>{t('ui.splash.thanks')}</p>
      </div>
    </div>
  );
};
